import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import FeedbackPage from './pages/FeedbackPage';
import StaffDashboard from './pages/StaffDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import PremiumPage from './pages/PremiumPage';
import SystemMonitorPage from './pages/SystemMonitorPage';
import RoomLogsPage from './pages/RoomLogsPage';
import AboutPage from './pages/AboutPage';
import JoinRoomHandler from './pages/JoinRoomHandler'; // Import the new component

import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
export const API = `${BACKEND_URL}/api`;

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios
        .get(`${API}/auth/me`)
        .then((res) => {
          setUser(res.data);
          // Apply dark mode based on user preference stored in localStorage for THIS user
          const userDarkModeKey = `darkMode_${res.data.id}`;
          const storedDarkMode = localStorage.getItem(userDarkModeKey);
          
          // If stored preference exists, use it; otherwise use server preference
          const shouldUseDarkMode = storedDarkMode !== null 
            ? storedDarkMode === 'true' 
            : res.data.dark_mode;
          
          if (shouldUseDarkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem(userDarkModeKey, 'true');
          } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem(userDarkModeKey, 'false');
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          // Remove dark mode when logged out
          document.documentElement.classList.remove('dark');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // NEW useEffect for global font loading
  useEffect(() => {
    const loadGlobalFonts = async () => {
      try {
        const response = await axios.get(`${API}/public/fonts`); // Use the correct public endpoint
        const fonts = response.data || [];
        
        let styleContent = '';
        fonts.forEach(font => {
          if (font.name && font.url && font.format) { // Ensure format is available
            styleContent += `
              @font-face {
                font-family: '${font.font_family}'; // Use font_family for CSS property
                src: url('${font.url}') format('${font.format}');
                font-display: swap;
              }
            `;
          }
        });

        if (styleContent) {
          // Check if style element already exists to prevent duplicates
          if (document.getElementById('global-custom-fonts')) {
            document.getElementById('global-custom-fonts').remove();
          }
          const styleElement = document.createElement('style');
          styleElement.id = 'global-custom-fonts';
          styleElement.innerHTML = styleContent;
          document.head.appendChild(styleElement);
          console.log('Global custom fonts loaded:', fonts.map(f => f.name).join(', '));
        }
      } catch (error) {
        console.error('Failed to load global custom fonts:', error);
      }
    };

    loadGlobalFonts();
  }, []); // Run once on component mount

  // Load Google AdSense only for non-premium users
  useEffect(() => {
    if (user && !user.is_premium) {
      // Check if script already loaded
      if (!document.querySelector('script[src*="adsbygoogle"]')) {
        const script = document.createElement('script');
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5180656415222741';
        script.async = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
        console.log('AdSense loaded for non-premium user');
      }
    } else if (user && user.is_premium) {
      console.log('AdSense NOT loaded - Premium user');
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/chat" replace />
              ) : <LandingPage setUser={setUser} />
            }
          />
          <Route
            path="/auth"
            element={
              user ? (
                user.role === 'staff' ? <Navigate to="/staff-dashboard" replace /> :
                <Navigate to="/chat" replace />
              ) : <AuthPage setUser={setUser} />
            }
          />
          <Route
            path="/profile"
            element={
              user && user.role !== 'guest' ? (
                <ProfilePage user={user} setUser={setUser} />
              ) : user && user.role === 'guest' ? (
                <Navigate to="/chat" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/profile/:userId"
            element={
              user ? (
                <ProfilePage user={user} setUser={setUser} isPublicView={true} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={<ForgotPasswordPage />}
          />
          <Route
            path="/verify-email"
            element={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold mb-4">Email Verified!</h2>
                <p className="text-gray-600 mb-6">Your email has been successfully verified. You can now login.</p>
                <a href="/auth" className="text-blue-600 hover:underline">Go to Login</a>
              </div>
            </div>}
          />
          <Route
            path="/chat"
            element={user ? <ChatPage user={user} setUser={setUser} /> : <Navigate to="/auth" replace />}
          />
          <Route path="/join/:inviteCode" element={<JoinRoomHandler />} />

          <Route
            path="/feedback"
            element={user ? <FeedbackPage /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/staff-dashboard"
            element={
              user && (user.role === 'staff' || user.role === 'super_admin') ? (
                <StaffDashboard user={user} />
              ) : (
                <Navigate to="/chat" replace />
              )
            }
          />
          <Route
            path="/super-admin-dashboard"
            element={
              user && user.role === 'super_admin' ? (
                <SuperAdminDashboard user={user} />
              ) : (
                <Navigate to="/chat" replace />
              )
            }
          />
          <Route
            path="/super-admin"
            element={
              user && user.role === 'super_admin' ? (
                <SuperAdminDashboard user={user} />
              ) : (
                <Navigate to="/chat" replace />
              )
            }
          />
          <Route
            path="/premium"
            element={user ? <PremiumPage user={user} setUser={setUser} /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/about"
            element={<AboutPage />}
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/terms-of-service"
            element={<TermsOfService />}
          />
          <Route
            path="/system-monitor"
            element={
              user && user.role === 'super_admin' ? (
                <SystemMonitorPage user={user} />
              ) : (
                <Navigate to="/chat" replace />
              )
            }
          />
          <Route
            path="/room-logs/:roomId"
            element={
              user && (user.role === 'super_admin' || user.role === 'staff') ? (
                <RoomLogsPage />
              ) : (
                <Navigate to="/chat" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
